import React, { useState, useEffect } from "react";
import { useCampaigns } from "@/hooks/useCampaigns";
import * as Icons from "@/components/ui/shared/Icons";
import clsx from "clsx";

// Define AccountCampaign type directly to avoid Database type conflicts
type AccountCampaign = {
  id: string
  account_id: string
  name: string
  assistant_id: string | null
  trigger_type: string
  trigger_keywords: string[] | null
  tags: string[] | null
  pipeline_id: string | null
  pipeline_stage_id: string | null
  is_active: boolean | null
  distribution_member_ids: string[] | null
  last_distribution_index: number | null
  created_at: string | null
  updated_at: string | null
}

interface CampaignSearchProps {
  onCampaignSelect?: (campaign: AccountCampaign) => void;
  onSearchChange?: (searchTerm: string) => void;
  placeholder?: string;
  styles?: string;
}

const CampaignSearch: React.FC<CampaignSearchProps> = ({ 
  onCampaignSelect, 
  onSearchChange,
  placeholder,
  styles = "w-[170px] sm:w-[220px] md:w-[270px] xl:w-[417px] min-h-[40px]"
}) => {
  const { campaigns } = useCampaigns();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCampaigns, setFilteredCampaigns] = useState<AccountCampaign[]>([]);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCampaigns([]);
      setShowResults(false);
      return;
    }

    const filtered = campaigns.filter(campaign => {
      const name = campaign.name.toLowerCase();
      const search = searchTerm.toLowerCase();
      
      return name.includes(search) || 
             campaign.trigger_keywords?.some((keyword: string) => 
               keyword.toLowerCase().includes(search)
             );
    });

    console.log('🔍 [CampaignSearch] Filtrando campanhas:', {
      searchTerm,
      total_campaigns: campaigns.length,
      filtered_count: filtered.length
    });

    setFilteredCampaigns(filtered.slice(0, 10)); // Limitar a 10 resultados
    setShowResults(true);
  }, [searchTerm, campaigns]);

  const handleCampaignClick = (campaign: AccountCampaign) => {
    console.log('👆 [CampaignSearch] Campanha selecionada:', campaign);
    onCampaignSelect?.(campaign);
    setSearchTerm(campaign.name);
    setShowResults(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    onSearchChange?.(e.target.value);
  };

  const handleInputFocus = () => {
    if (searchTerm.trim() && filteredCampaigns.length > 0) {
      setShowResults(true);
    }
  };

  const handleInputBlur = () => {
    // Delay para permitir clique nos resultados
    setTimeout(() => setShowResults(false), 200);
  };

  return (
    <div className="relative">
      <div
        className={clsx(
          "gap-2 h-11 rounded-xl border border-[#E5E7EB] bg-white px-3 flex justify-start items-center shadow-sm transition-colors duration-200",
          "focus-within:border-[#2563EB] focus-within:ring-[#2563EB]/20 focus-within:ring-[3px]",
          "dark:border-[#3F3F46] dark:bg-[#27272A] dark:shadow-none dark:focus-within:border-[#60A5FA] dark:focus-within:ring-[#2563EB]/30",
          styles
        )}
      >
        <Icons.search className="w-[16px] h-[16px] text-[#64748B] dark:text-[#A1A1AA]" />
        <input
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          className="grow-1 text-[16px] font-normal text-[#0F172A] placeholder:text-[#94A3B8] w-full h-full border-none focus:outline-none bg-transparent dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
        />
      </div>

      {/* Resultados da busca */}
      {showResults && filteredCampaigns.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E5E7EB] rounded-xl shadow-md z-50 max-h-[300px] overflow-y-auto dark:bg-[#27272A] dark:border-[#3F3F46] dark:shadow-none">
          {filteredCampaigns.map((campaign) => (
            <div
              key={campaign.id}
              onClick={() => handleCampaignClick(campaign)}
              className="flex items-center gap-3 p-3 hover:bg-[#F1F5F9] cursor-pointer transition-colors duration-200 border-b border-[#E5E7EB] last:border-b-0 dark:hover:bg-[#3F3F46] dark:border-[#3F3F46]"
            >
              <div className="w-[32px] h-[32px] bg-[#EEF4FF] rounded-full flex items-center justify-center dark:bg-blue-500/15">
                <span className="text-xs font-medium text-[#2563EB] dark:text-[#60A5FA]">
                  {campaign.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <p className="text-[14px] font-medium text-[#0F172A] truncate dark:text-[#FAFAFA]">{campaign.name}</p>
                <p className="text-[12px] text-[#64748B] truncate dark:text-[#71717A]">
                  {campaign.trigger_type === 'any' ? 'Qualquer mensagem' : 
                   campaign.trigger_type === 'contains' ? 'Contém palavras-chave' : 
                   'Inicia com palavras-chave'}
                </p>
              </div>
              {campaign.is_active ? (
                <span className="text-xs bg-[#ECFDF5] text-[#16A34A] px-2 py-1 rounded-lg dark:bg-green-500/10 dark:text-[#22C55E]">
                  Ativa
                </span>
              ) : (
                <span className="text-xs bg-[#F1F5F9] text-[#64748B] px-2 py-1 rounded-lg dark:bg-[#3F3F46] dark:text-[#A1A1AA]">
                  Inativa
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CampaignSearch;
